import React, { useMemo } from 'react'

import { I18n, hooks } from '@front/volcanion'
import Provider from '@front/volcanion/provider'
import Contact from '@front/volcanion/classes/contact'

import StorageManager from '@front/squirtle/services/storage'
import IdentityUtils from '@front/squirtle/utils/identity'
import { SVG, GIF, Image, Loader, InformationDialog, ConfirmationDialog, ToastContainer } from '@front/squirtle'
import useBlobHandler from '@front/squirtle/hooks/useBlobHandler'
import useNotification from '@front/squirtle/hooks/useNotification'
import useRouter from '@front/squirtle/components/Router/useRouter'

import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'

import moment from 'moment'
import 'moment/locale/fr'

import info from '../info'
import config from '../constants'
import getTranslations from './translations'
import Pages from './pages'
import TitlePage from '@abra/elements/TitlePage'

I18n.setI18nConfig(getTranslations)
moment.locale(info.locale)

const OrangeContact = new Contact(null, 'orange', true, { disable_retry: true })

OrangeContact.init('api-mediation.flexiblecontactcenter.orange-business.com', 443, {
  'Content-Type': 'application/json',
  'Accept-Language': 'fr',
}, { axios: { withCredentials: true } })

// NOTE: `overrides` is passed as the 3rd arg to `IdentityUtils.getTheme(identity, mode, overrides)`
// but `getTheme` currently ignores it (squirtle/utils/identity.js:32 only consumes 2 args).
// Kept here for the day `getTheme` is extended to merge overrides — see follow-up ticket.
// The intended MuiToolbar minHeight 50px override currently lives in squirtle/themes/components.js
// as a tech-debt workaround until `getTheme` honors this argument.
const overrides = {
  MuiInputLabel: {
    defaultProps: {
      size: 'small',
      shrink: true,
    }
  }
}

const Main = () => {
  const identity = hooks.useIdentity()
  const isReady = hooks.useReadyStatus()
  const [mode] = hooks.useGlobalState('mode')
  const theme = useMemo(() => IdentityUtils.getTheme(identity, mode, overrides), [identity, mode, overrides])

  return (
    <Loader isLoading={!isReady} nativeLoader>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Pages />
        <TitlePage />
        <InformationDialog name={'information_dialog'} />
        <InformationDialog name={'information_error_dialog'} icon='error' _icon={{ color: 'primary' }} />
        <ConfirmationDialog name={'confirmation_dialog'} />
        <ConfirmationDialog name={'confirmation_error_dialog'} icon='error' _icon={{ color: 'primary' }} />
      </ThemeProvider>
    </Loader>
  )
}

const MemoMain = React.memo(Main)
const App = () => {
  return (
    <Provider
      storage={{ whitelist: [], manager: StorageManager }}
      relay={{ info, config }}
      global={{ persist: ['persist'] }}
      main={{
        useRouter,
        useNotification,
        useBlobHandler,
        ImageRenderer: Image,
        SVGRenderer: SVG,
        GIFRenderer: GIF,
        ToastContainerRenderer: ToastContainer
      }}
    >
      <MemoMain />
    </Provider>
  );
};

export default React.memo(App);
export {
  OrangeContact
}
